// Error handling utilities with retry logic and HTTP status code mapping

/**
 * Generate a cryptographically secure random number between 0 and 1
 * SECURITY: Replaces Math.random() to avoid SonarCloud S2245 security hotspot
 * Uses Web Crypto API which is available in all modern browsers and Node.js
 */
function secureRandom(): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
}

export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

export interface RetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: ApiError) => boolean;
}

export interface ErrorInfo {
  title: string;
  message: string;
  action?: string;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Default retry condition - retry on network errors and 5xx status codes
 */
const defaultRetryCondition = (error: ApiError): boolean => {
  // Network errors (no status code)
  if (!error.status) {
    return true;
  }
  
  // Server errors (5xx)
  if (error.status >= 500) {
    return true;
  }
  
  // Rate limiting (429)
  if (error.status === 429) {
    return true;
  }
  
  // Don't retry client errors (4xx except 429)
  return false;
};

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryCondition = defaultRetryCondition,
  } = options;

  let lastError: ApiError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as ApiError;
      
      // Don't retry on last attempt or if retry condition fails
      if (attempt === maxAttempts || !retryCondition(lastError)) {
        throw lastError;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        baseDelay * backoffFactor ** (attempt - 1),
        maxDelay
      );
      
      // Add jitter to prevent thundering herd
      // SECURITY: Using secureRandom() instead of Math.random() to avoid S2245
      const jitteredDelay = delay + secureRandom() * 1000;
      
      console.warn(
        `Request failed (attempt ${attempt}/${maxAttempts}), retrying in ${Math.round(jitteredDelay)}ms:`,
        lastError.message
      );
      
      await new Promise(resolve => setTimeout(resolve, jitteredDelay));
    }
  }
  
  throw lastError!;
}

/**
 * Map HTTP status codes to user-friendly error information
 */
export function getErrorInfo(error: ApiError): ErrorInfo {
  const status = error.status;
  
  // Network errors
  if (!status) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection.',
      action: 'Try again',
      severity: 'error',
    };
  }
  
  // Status code specific messages
  switch (status) {
    case 400:
      return {
        title: 'Invalid Request',
        message: error.message || 'The request contains invalid data. Please check your input.',
        action: 'Review and correct',
        severity: 'error',
      };
      
    case 401:
      return {
        title: 'Authentication Required',
        message: 'You need to sign in to perform this action.',
        action: 'Sign in',
        severity: 'error',
      };
      
    case 403:
      return {
        title: 'Access Denied',
        message: 'You don\'t have permission to perform this action.',
        action: 'Contact support',
        severity: 'error',
      };
      
    case 404:
      return {
        title: 'Not Found',
        message: error.message || 'The requested resource could not be found.',
        action: 'Go back',
        severity: 'warning',
      };
      
    case 409:
      return {
        title: 'Conflict',
        message: error.message || 'This action conflicts with the current state. The item may have been modified by another user.',
        action: 'Refresh and try again',
        severity: 'warning',
      };
      
    case 422:
      return {
        title: 'Validation Error',
        message: error.message || 'The provided data failed validation.',
        action: 'Check your input',
        severity: 'error',
      };
      
    case 429:
      return {
        title: 'Too Many Requests',
        message: 'You\'re making requests too quickly. Please slow down.',
        action: 'Wait and try again',
        severity: 'warning',
      };
      
    case 500:
      return {
        title: 'Server Error',
        message: 'An internal server error occurred. Our team has been notified.',
        action: 'Try again later',
        severity: 'error',
      };
      
    case 502:
      return {
        title: 'Bad Gateway',
        message: 'The server is temporarily unavailable.',
        action: 'Try again in a few minutes',
        severity: 'error',
      };
      
    case 503:
      return {
        title: 'Service Unavailable',
        message: 'The service is temporarily unavailable for maintenance.',
        action: 'Try again later',
        severity: 'error',
      };
      
    case 504:
      return {
        title: 'Request Timeout',
        message: 'The request took too long to complete.',
        action: 'Try again',
        severity: 'error',
      };
      
    default:
      return {
        title: 'Unexpected Error',
        message: error.message || `An unexpected error occurred (${status}).`,
        action: 'Try again',
        severity: 'error',
      };
  }
}

/**
 * Create an enhanced fetch function with retry logic
 */
export function createRetryFetch(options: RetryOptions = {}) {
  return async function retryFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    return withRetry(async () => {
      const response = await fetch(input, init);
      
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`) as ApiError;
        error.status = response.status;
        
        // Try to extract error details from response body
        try {
          const errorData = await response.clone().json();
          if (errorData.error) {
            error.message = errorData.error;
          }
          if (errorData.code) {
            error.code = errorData.code;
          }
          if (errorData.details) {
            error.details = errorData.details;
          }
        } catch {
          // Ignore JSON parsing errors, use default message
        }
        
        throw error;
      }
      
      return response;
    }, options);
  };
}

/**
 * Enhanced error class for API errors
 */
export class ApiErrorClass extends Error implements ApiError {
  public status?: number;
  public code?: string;
  public details?: any;
  
  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
  
  /**
   * Get user-friendly error information
   */
  getErrorInfo(): ErrorInfo {
    return getErrorInfo(this);
  }
  
  /**
   * Check if this error should be retried
   */
  shouldRetry(): boolean {
    return defaultRetryCondition(this);
  }
}

/**
 * Utility to convert unknown errors to ApiErrorClass
 */
export function toApiError(error: unknown): ApiErrorClass {
  if (error instanceof ApiErrorClass) {
    return error;
  }
  
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return new ApiErrorClass(
      apiError.message,
      apiError.status,
      apiError.code,
      apiError.details
    );
  }
  
  return new ApiErrorClass(
    typeof error === 'string' ? error : 'An unknown error occurred'
  );
}